<template>
	<Form :validation-schema="validationSchema" class="form-container" @submit="onSubmit" v-slot="{ errors }">
		<div v-for="field in fields" :key="field.name" :class="['form-input', field.width]">
			<label :for="field.name">{{ field.label }}</label>
			<Field
				v-if="field.type === 'select'"
				as="select"
				:id="field.name"
				:name="field.name"
				v-model="formData[field.name]"
				:disabled="isFieldDisabled(field)"
			>
				<option></option>
				<option v-for="option in field.options" :key="option.id" :value="option.id">
					{{ option.name }}
				</option>
			</Field>
			<Field
				v-else
				:id="field.name"
				:name="field.name"
				:type="field.type"
				v-model="formData[field.name]"
				:disabled="isFieldDisabled(field)"
			/>
			<ErrorMessage v-show="mode !== 'view'" :name="field.name" class="error" />
		</div>

		<button
			v-if="mode === 'edit'"
			:class="submitButtonClass"
			type="submit"
			:disabled="!isFormValid(errors) || isSubmitting"
		>
			{{ isPostDisbursementChange && !postDisbursementChange ? 'Create Post-Disbursement Change' : 'Submit' }}
		</button>

		<button
			v-if="mode === 'edit' && editChangeVisible"
			class="secondary col-6"
			type="button"
			:disabled="!isFormValid(errors) || isSubmitting"
			@click.prevent="showPostDisbursementChangeModal"
		>
			Edit Post-Disbursement Change
		</button>

		<PostDisbursementChangeForm
			ref="postDisbursementChangeFormRef"
			v-show="postDisbursementChangeFormVisible"
			:isSubmitting="isSubmitting"
			:fields="changeFormFields"
			:validationSchema="changeValidationSchema"
			@apply="applyPostDisbursementChange"
			@close="closePostDisbursementChangeModal"
		/>
	</Form>
</template>

<script setup lang="ts">
	import { defineProps, reactive, ref, watchEffect, computed, watch } from 'vue';
	import type { ComponentPublicInstance } from 'vue';
	import { Form, Field, ErrorMessage } from 'vee-validate';
	import { Schema } from 'yup';
	import { PayoffPostDisbursementChange } from '@/models/loans';
	import PostDisbursementChangeForm from './PostDisbursementChangeForm.vue';
	import { usePostDisbursementChangeFormFields } from '@/composables/loan/refi/usePostDisbursementChangeForm';
	import { usePostDisbursementChangeData } from '@/composables/loan/refi/usePostDisbursementChangeData';

	interface FormField {
		name: string;
		label: string;
		type: string;
		value: any;
		width: string;
		showOnEdit: boolean;
		disabledOnEdit: boolean;
	}

	interface PostDisbursementChangeFormInstance {
		clearFormData: () => void;
	}

	const props = defineProps<{
		onSubmit: (
			formData: Record<string, any>,
			postDisbursementChange: PayoffPostDisbursementChange | null
		) => Promise<void>;
		originalDisbursementAmount: number;
		isSubmitting: boolean;
		fields: Array<FormField>;
		validationSchema: Schema;
		mode: 'edit' | 'view';
	}>();

	const formData = reactive<Record<string, any>>({});
	const postDisbursementChangeFormRef = ref<(ComponentPublicInstance & PostDisbursementChangeFormInstance) | null>(
		null
	);

	const postDisbursementChange = ref<PayoffPostDisbursementChange | null>(null);
	const postDisbursementChangeFormVisible = ref<boolean>(false);

	const { payoffDisbursementTypeOptions } = usePostDisbursementChangeData();

	const { changeFormFields, changeValidationSchema } = usePostDisbursementChangeFormFields({
		disbursementAmount: props.originalDisbursementAmount,
		payoffDisbursementTypeOptions: payoffDisbursementTypeOptions
	});

	const isFormValid = computed(() => {
		return (errors: Partial<Record<string, string | undefined>>) => errors && Object.keys(errors).length === 0;
	});

	const isPostDisbursementChange = computed(() => formData['payoffDisbursementStatusId'] === 5);
	const editChangeVisible = computed(() => isPostDisbursementChange.value && postDisbursementChange.value);
	const submitButtonClass = computed(() => ['submit-button', editChangeVisible.value ? 'col-6' : 'col-12']);

	const showPostDisbursementChangeModal = () => (postDisbursementChangeFormVisible.value = true);
	const closePostDisbursementChangeModal = () => (postDisbursementChangeFormVisible.value = false);

	function applyPostDisbursementChange(formData: Record<string, any>) {
		postDisbursementChange.value = { ...formData } as PayoffPostDisbursementChange;
	}

	function isFieldDisabled(field: FormField) {
		const disabledOnEdit = props.mode === 'edit' && field.disabledOnEdit === true;

		return props.mode === 'view' || props.isSubmitting || disabledOnEdit;
	}

	async function onSubmit() {
		if (isPostDisbursementChange.value && !postDisbursementChange.value) {
			showPostDisbursementChangeModal();
		} else if (props.onSubmit) {
			await props.onSubmit({ ...formData }, postDisbursementChange.value);
			clearPostDisbursementChange();
		}
	}

	function clearPostDisbursementChange() {
		postDisbursementChange.value = null;
		postDisbursementChangeFormRef.value?.clearFormData();
	}

	watch(
		() => formData['payoffDisbursementStatusId'],
		(newValue, oldValue) => {
			if (oldValue === 5 && newValue !== 5) {
				clearPostDisbursementChange();
			}
		}
	);

	watchEffect(() => {
		props.fields.forEach((field) => {
			formData[field.name] = field.value;
		});
	});
</script>

<style scoped src="@/assets/css/loans/form.css"></style>
